import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImpersonationBanner } from '@/features/auth/ImpersonationBanner';
import { SuspendedBanner } from './SuspendedBanner';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { RaadLogo } from '@/components/RaadLogo';
import { LangToggle } from '@/components/LangToggle';
import { OfflineBanner } from '@/features/pwa/OfflineBanner';
import { InstallPrompt } from '@/features/pwa/InstallPrompt';
import { PushToggle } from '@/features/push/PushToggle';
import { usePushMessages } from '@/features/push/usePushMessages';
import { mapIntentHandlers, preconnectGoogleTiles } from '@/features/map/prefetchMap';
import { hasGoogleKey } from '@/features/map/basemap';
import { useAuth } from '@/features/auth/useAuth';
import { useUnreadAlertCount, useAlertsFeedSync } from '@/features/alerts/useAlerts';
import { OverviewGreeting } from '@/features/overview/OverviewGreeting';
import { OverviewSearch } from '@/features/overview/OverviewSearch';
import type { Role } from '@/features/auth/authClient';

// Icon paths ported from the design canvas nav (screens 1i / 1e).
//
// The nav lists ONLY routes that exist and work. No disabled item, no
// "coming soon", no placeholder for a screen that isn't built — see CLAUDE.md
// "No control for a feature that doesn't exist": a disabled control promises a
// feature and dates the promise. Nav grows as the product does.
const ICONS = {
  overview: 'M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h7v7h-7z',
  livemap: 'M22 2 11 13 M22 2l-7 20-4-9-9-4z',
  vehicles: 'M1 3h15v13H1z M16 8h4l3 3v5h-7z M5.5 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M18.5 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  trips: 'M5 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M19 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M7 19h8a4 4 0 0 0 0-8H9a4 4 0 0 1 0-8h8',
  // The zone/layers glyph from the canvas nav (screen 5c, `I.zone`).
  zones: 'M3 7l6-4 6 4 6-3v13l-6 3-6-4-6 4z',
  // A MAP PIN for Places (slice 24) — a named point, which is what a place is.
  // Distinct from every other glyph in this rail: the layers stack (zones), the
  // bell (alerts), the spanner (maintenance), the gauge (behaviour), the chain
  // (sharing), the document (reports) and the gear (settings) all already mean
  // something else, and a nav whose icons read as duplicates is a nav the eye
  // cannot scan. It deliberately DOES echo the pin on `CoordChip`'s open-in-maps
  // affordance, because both mean "a point on the ground".
  places: 'M12 22s7-6.8 7-12a7 7 0 1 0-14 0c0 5.2 7 12 7 12z M12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  // The bell glyph from the canvas nav (screen 1o).
  alerts: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9 M13.7 21a2 2 0 0 1-3.4 0',
  // The wrench glyph for Maintenance (slice 28) — a service reminder is a
  // spanner job, and it must not reuse the reports document or the zones layers
  // glyph, which already mean something else in this rail.
  maintenance: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-.6-.6-2.4z',
  // A GAUGE for Behaviour (slice 29) — how the vehicle was driven. Deliberately
  // not the spanner (maintenance) or the document (reports), both of which
  // already mean something else in this rail; an icon that reads as a duplicate
  // is a nav the eye cannot scan.
  behaviour: 'M12 21a9 9 0 1 1 9-9 M12 12l4.5-4.5 M12 12h.01',
  // A TWO-PERSON "users" glyph for Drivers (slice D1). Distinct from every other
  // glyph in this rail: the gauge (behaviour), the truck (vehicles), the layers
  // (zones) and the document (reports) all mean something else, and the single
  // person on the DriverChip is one person where this is the roster. A nav whose
  // icons read as duplicates is a nav the eye cannot scan.
  drivers:
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
  // A LINK glyph for Sharing (slice 22) — two chain links. Distinct from every
  // other glyph in this rail: the gauge (behaviour), the spanner (maintenance)
  // and the document (reports) all already mean something else, and a nav whose
  // icons read as duplicates is a nav the eye cannot scan.
  shares:
    'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1 M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  // The document/report glyph from the canvas reports nav (screen 1p).
  reports: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z M14 3v6h6 M8 13h8 M8 17h8 M8 9h2',
  // The gear glyph from the canvas nav (screen 1r).
  // Horizontal ellipsis — the overflow glyph for More (canvas 1g).
  more: 'M6 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M18 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
} as const;

const NAV_ROUTES: Record<keyof typeof ICONS, string> = {
  overview: '/',
  livemap: '/map',
  vehicles: '/vehicles',
  trips: '/trips',
  zones: '/zones',
  places: '/places',
  alerts: '/alerts',
  maintenance: '/maintenance',
  behaviour: '/behaviour',
  drivers: '/drivers',
  shares: '/shares',
  reports: '/reports',
  settings: '/settings',
  more: '/more',
};

// The two navs are SEPARATE lists on purpose, not one array rendered twice.
// Mahad's requirement (canvas 1e): the desktop sidebar carries a Trips entry, but
// the PWA keeps trips INSIDE vehicle detail and has no trips tab on the phone's
// bottom bar. A single shared array would put Trips on both — the opposite of what
// was asked — so the desktop list can carry a destination the mobile list does
// not.
//
// 🔴 THE PHONE DOES REACH `/trips`, AND THIS COMMENT USED TO DENY IT. It said
// "the top-level Trips page is a desktop fleet view; the phone reaches trips
// through the vehicle-detail Trips tab" — true when written, false the moment
// 17f added Trips to the More overflow screen (`routes/More.tsx`). It stayed
// wrong for two slices, and 17h read it and reasoned correctly from it into a
// production bug: it scoped the phone replay layout to `!embedded` because the
// embedded call site "is the desktop Trips page". Trips is absent from the four
// phone TAB-BAR items; it is not absent from the phone.
// Zones is DESKTOP-ONLY as a NAV ENTRY (Mahad, slice 12): "A phone tab bar with
// four items is usable; with six it isn't. Geofence creation is desktop-first
// anyway, and viewing a fence is something you do on the map, not on a list
// screen." It is in DESKTOP_NAV and deliberately NOT in MOBILE_NAV; the two are
// separate lists precisely so a new entry cannot leak to the phone (the slice-11
// trap).
//
// 🔴 THE OLD COMMENT HERE SAID THE SCREEN "STAYS REACHABLE BY URL ON ANY
// VIEWPORT". THAT WAS FALSE, AND IT HID A REAL DEFECT FOR THREE SLICES.
// `app.raadtelematics.com` ships `display: standalone` (manifest), and standalone
// REMOVES THE ADDRESS BAR. The product's mobile form is the installed PWA — the
// real user is "a fleet manager on an Android phone" — so for that user there was
// no URL to type and NO in-app link to /zones anywhere. A page we built and hid
// from the people it is for. The rule earned: a claim about reachability that
// holds in a browser tab and not in the installed PWA is a claim about the WRONG
// RUNTIME (same family as slice 04's service worker on a shared origin).
//
// Slice 17f fixes it: `more` joins MOBILE_NAV by an EXPLICIT edit here — the same
// pattern as Alerts (slice 15b) and Settings (slice 16), never by merging the
// arrays — and Zones and Trips are reached THROUGH the More screen, not as tab
// items. So the bar gains exactly one destination, Trips/Zones still never appear
// on it, and the two-array guarantee is intact. `AppShell.test.tsx` asserts that
// outcome directly: 5 items in the tab bar, and `nav.trips`/`nav.zones` ABSENT
// from it.
//
// ⚠️ PROVENANCE: the More shape comes from canvas 1g/2d, but every mobile
// artboard is labelled `Consumed by: — (Mobile app … — Phase 4 (Flutter))`. We
// took the SHAPE, not a spec. Do NOT cite 1g as binding on this PWA.
// Alerts is on BOTH navs (slice 15b). It was DESKTOP-ONLY through slice 13 only
// because it shipped after the phone tab bar was set, never for a product reason —
// a fleet manager on a phone needs to see a zone crossing and its unread count as
// much as, or more than, a desktop user. Four items is exactly the tab-bar limit
// Mahad named ("usable with four, not with six"), so Alerts fills the fourth slot
// and Trips/Geofences stay desktop-only. The two arrays remain SEPARATE, not one
// list rendered twice: that is what keeps a new desktop entry (Trips, Geofences)
// from leaking onto the phone (the slice-11 trap). Alerts joins MOBILE_NAV by an
// explicit edit here, not by merging the lists.
// Settings (slice 16) joins DESKTOP_NAV and NOT MOBILE_NAV, by an explicit edit
// here rather than by merging the lists. The phone tab bar is at Mahad's stated
// limit of four ("usable with four, not with six"), and a settings screen does
// not outrank Overview / Live map / Vehicles / Alerts for the fourth slot.
//
// 🔴 BUT IT IS NOT DESKTOP-ONLY, and the distinction matters: the real user is a
// fleet manager on an Android phone, and "change your password" must not be a
// desktop-only capability. The phone reaches it through the GEAR IN THE TOP BAR
// (`lg:hidden`, beside sign-out), which is why that control exists — the mobile
// layout has no sidebar and no drawer to hang an account row on. Desktop gets
// the sidebar entry instead, so neither viewport shows two doors to one screen.
// Reports (slice 26) joins DESKTOP_NAV and NOT MOBILE_NAV, by an explicit edit
// here rather than by merging the lists — the phone tab bar is at Mahad's stated
// four-item limit, so Reports is reached through the More screen (the same path
// as Trips and Zones). It is UNCONDITIONAL: every plan sees every report (Mahad,
// 1 Sep), so there is no plan gate on the entry.
// Maintenance (slice 28) joins DESKTOP_NAV — AFTER Zones, BEFORE Reports — and
// NOT MOBILE_NAV, by an explicit edit here rather than by merging the lists. The
// phone tab bar is at Mahad's stated four-item limit, so Maintenance is reached
// through the More screen, the same path as Trips, Zones and Reports.
//
// 🔴 AN ADDITION, NOT A RENAME. Every existing entry keeps its label, its route
// and its position; this is one new destination in the gap the brief names.
// Slice 29 adds `behaviour` to DESKTOP_NAV and NOT to MOBILE_NAV, by an explicit
// edit here rather than by merging the two lists — the phone tab bar is at
// Mahad's stated limit of four, so /behaviour is reached from More on a phone
// (More.tsx carries the row, and in the installed PWA that row is the ONLY path
// to it: standalone display has no address bar).
// Slice 24 adds `places` to DESKTOP_NAV — immediately AFTER Zones, because a
// place is the thing customers will reach for while thinking about zones and the
// two must not be confused for each other — and NOT to MOBILE_NAV, by an explicit
// edit here rather than by merging the lists. The tab bar is full at five; the
// phone reaches Places through More, which is the same path Maintenance,
// Behaviour, Sharing and Reports take. The entry lands WITH the screen, never
// before it.
//
// 🔴 THE LABEL AND ITS SOMALI TERM ARE ESCALATION E3 IN THE SLICE PLAN and are
// Mahad's to confirm. They live in `i18n/{en,so}.json` under `nav.places` and
// `places.title` ONLY — no code names the feature — so a different word is one
// edit to two JSON leaves, with nothing to hunt for.
// Slice D1 adds `drivers` to DESKTOP_NAV — AFTER `behaviour`, with which it shares
// the canvas 3b artboard — and NOT to MOBILE_NAV, by an explicit edit here rather
// than by merging the two lists (the slice-11 trap). The phone tab bar is at
// Mahad's stated limit of four, so /drivers is reached from More on a phone
// (More.tsx carries the row, and in the installed PWA that row is the ONLY path
// to it: standalone display has no address bar). An addition, not a rename: every
// existing entry keeps its label, route and position. The entry lands WITH the
// screen, never before it.
const DESKTOP_NAV = ['overview', 'livemap', 'vehicles', 'trips', 'alerts', 'zones', 'places', 'shares', 'maintenance', 'behaviour', 'drivers', 'reports', 'settings'] as const;
const MOBILE_NAV = ['overview', 'livemap', 'vehicles', 'alerts', 'more'] as const;

function NavIcon({ d }: { d: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  const first = parts[0]![0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '';
  return (first + second).toUpperCase();
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { state, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  // The Overview owns the top-bar greeting and search (canvas 1i draws them in
  // this one bar). They render ONLY on the Overview route, so the greeting never
  // appears on Live Map / Vehicles / Trips, and no other route mounts the live
  // query those pieces carry. `useLocation().pathname` is app-relative (the
  // router's basename is already stripped), so the Overview index is exactly '/'.
  const isOverview = useLocation().pathname === '/';

  /**
   * THE SHELL PUBLISHES ITS OWN CHROME HEIGHT as `--raad-chrome`, measured off the
   * real rendered element rather than hardcoded.
   *
   * A route that needs to size itself to "the viewport minus everything above me"
   * needs that number, and it is NOT a constant. The top bar is a steady 75 px on
   * desktop — but `OfflineBanner` / `InstallPrompt` sit above it and add ~57 px WHEN
   * THEY APPLY. Measuring the same page gave 75 px in one run and 132 px in another,
   * which is precisely the shape that becomes a wrong magic constant six weeks
   * later. A ResizeObserver keeps it true when the banner appears or disappears
   * mid-session, so nothing has to remember to update a number.
   */
  // Slice 56f — warm the Google tile connection from the SIGNED-IN shell only (the
  // static index.html also serves the Google-free share page). The map CODE is NOT
  // fetched here: it downloads on intent (mapIntentHandlers on the Live-map links),
  // keeping maplibre off the landing screen (check-overview-render.mjs M-4).
  useEffect(() => {
    preconnectGoogleTiles(document, hasGoogleKey());
  }, []);

  const chromeRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = chromeRef.current;
    if (!el) return;
    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--raad-chrome', `${h}px`);
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The unread-alerts badge on the desktop Alerts nav item. Enabled only when
  // authenticated, and CLEARED by marking read — the mark-read mutation
  // invalidates this query, so the number drops the moment alerts are read rather
  // than sticking as a badge people learn to ignore.
  const authed = state.status === 'authenticated';
  const unreadQuery = useUnreadAlertCount({ enabled: authed });
  // 17h — one mount, app-wide: when the badge count changes, the alerts list
  // re-reads. Mounted HERE because this is where the badge already polls.
  useAlertsFeedSync();
  const unread = unreadQuery.data?.unread ?? 0;

  // A push arrival re-reads the alerts feed and the unread badge immediately,
  // rather than waiting on the 60 s poll (task item 6).
  usePushMessages();

  const user = state.status === 'authenticated' ? state.user : null;
  const name = user?.name ?? '';
  const roleLabel = t(`shell.role.${(user?.role ?? 'viewer') as Role}`);
  const langLabel = i18n.language.toUpperCase();

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  }

  const Nav = (
    <nav className="flex flex-col gap-0.5" aria-label={t('nav.overview')}>
      {DESKTOP_NAV.map((key) => (
        <NavLink
          key={key}
          to={NAV_ROUTES[key]}
          end={NAV_ROUTES[key] === '/'}
          {...(key === 'livemap' ? mapIntentHandlers : {})}
          // 17q — between the `lg` and `xl` breakpoints the sidebar is a 64px
          // ICON RAIL, so the item centres its icon and drops padding/label; at
          // `xl` it is the full 230px row again. The breakpoint PIXEL VALUES are
          // deliberately not restated here: 17j made `--breakpoint-lg` a single
          // declaration read by one module, and a comment naming the number is a
          // second copy that drifts silently the day the number moves. Name the
          // breakpoint, never its width. `aria-label` is ADDED, not "kept": the
          // accessible name came from the visible label text, which the rail
          // hides — without this the rail ships nameless for screen readers. The
          // unread count keeps its OWN accessible name on the inline badge below
          // (unreadBadge.test: the badge carries the count as name and text), so
          // it is not folded in here.
          aria-label={t(`nav.${key}`)}
          className={({ isActive }) =>
            [
              'flex items-center rounded-lg text-[13px]',
              'lg:justify-center lg:gap-0 lg:px-0 lg:py-2.5',
              'xl:justify-start xl:gap-2.5 xl:px-3',
              isActive
                ? 'bg-raad-primary/15 font-semibold text-raad-accent'
                : 'font-medium text-raad-muted hover:text-raad-text',
            ].join(' ')
          }
        >
          {/* ONE badge, overlaid on the icon at every desktop width (rail AND
              full). A single element carries the accessible name at both widths —
              two breakpoint-split badges each go `display:none` at the other
              width, taking the count out of the screen-reader tree in the band
              where they are hidden (the rail regression review caught). It is the
              only element bearing this name, so `unreadBadge.test` (jsdom, no CSS,
              both-in-DOM) still finds exactly one, with text `7`. */}
          <span className="relative flex items-center justify-center">
            <NavIcon d={ICONS[key]} />
            {key === 'alerts' && unread > 0 ? (
              <span
                aria-label={t('alerts.unreadCount', { count: unread })}
                className="absolute -right-2 -top-1.5 min-w-4 rounded-full bg-raad-primary px-1 text-center text-[9px] font-bold leading-4 text-raad-onprimary"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </span>
          <span className="hidden flex-1 xl:block">{t(`nav.${key}`)}</span>
        </NavLink>
      ))}
    </nav>
  );

  // Mobile has no sidebar, so the live routes get a bottom tab bar — the only
  // way a phone user reaches Vehicles. It carries FEWER items than the sidebar:
  // no Trips or Geofences tab (both desktop-first), but Alerts is here (slice 15b)
  // so the phone user has an unread indicator, not just the desktop sidebar.
  // `MOBILE_NAV`, not `DESKTOP_NAV`. The badge reads the SAME `unread` value the
  // desktop nav uses — the shared `useUnreadAlertCount` query above — never a
  // second, client-side recount (seam S8, the copied-predicate defect slice 15
  // exists to have prevented).
  const MobileNav = (
    <nav
      aria-label={t('nav.overview')}
      data-testid="mobile-tabbar"
      className="fixed inset-x-0 bottom-0 z-10 flex border-t border-raad-border bg-raad-deep pb-[var(--raad-safe-b)] lg:hidden"
    >
      {MOBILE_NAV.map((key) => (
        <NavLink
          key={key}
          to={NAV_ROUTES[key]}
          end={NAV_ROUTES[key] === '/'}
          {...(key === 'livemap' ? mapIntentHandlers : {})}
          className={({ isActive }) =>
            [
              'flex min-h-[var(--raad-nav-h)] flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-semibold',
              isActive ? 'text-raad-accent' : 'text-raad-muted',
            ].join(' ')
          }
        >
          <span className="relative">
            <NavIcon d={ICONS[key]} />
            {key === 'alerts' && unread > 0 ? (
              <span
                aria-label={t('alerts.unreadCount', { count: unread })}
                className="absolute -right-2.5 -top-1.5 min-w-4 rounded-full bg-raad-primary px-1 text-center text-[9px] font-bold leading-4 text-raad-onprimary"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </span>
          {t(`nav.${key}`)}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <>
      {/* Read-only impersonation (slice 17c). OUTSIDE the shell's flex row and
          sticky, so it sits above every route rather than inside one column —
          a staff member must see whose account this is from any screen, not
          only the one they landed on. Renders nothing for an ordinary session. */}
      <ImpersonationBanner />
    {/* 🔴 AT `lg` THE SHELL IS EXACTLY THE VIEWPORT AND NEVER SCROLLS (17n).
           Mahad, 28 Aug: "the app shell should be exactly the viewport and never
           scroll. Chrome flex-none, route frame flex-1, scrolling happens INSIDE
           columns only. Then no screen can do this."

           WHAT IT REPLACES. Bounding each ROUTE was whack-a-mole and did not
           even hold: on /trips with 60 trips and a selection,
           documentElement.scrollHeight was 9792 against a 900 viewport while
           every box in the chain measured bounded (frame 768, column 768,
           scroller 652). Hiding the trip list dropped it to 900; hiding the
           aside or the replay pane changed nothing. The layout and the
           document's scroll area disagreed, and chasing that disagreement is
           not worth it when the shape below makes it unreachable.

           `overflow-hidden` here is the whole guarantee: the document cannot
           scroll, so no route can drag the sidebar and the user's own name off
           the top of the screen no matter what leaks inside it. Scrolling moves
           into `main` and into columns, which is where it was always meant to
           be. `lg:` only — the phone is a scrolling document by design. */}
      <div className="min-h-dvh bg-raad-bg text-raad-text lg:flex lg:h-dvh lg:min-h-0 lg:overflow-hidden">
      {/* Sidebar — lg and up (canvas 1i). */}
      {/* The sidebar scrolls ITSELF. With the shell pinned it can no longer
           push the page, so a sidebar taller than the window (a long nav, a large
           text setting) must have somewhere to go — otherwise it would simply be
           clipped and the user's own name would be unreachable rather than merely
           off-screen. `min-h-0` is what lets it shrink to the shell. */}
      {/* 17q — ICON RAIL between lg and xl. The full 230px sidebar is 16.8% of a
          1366 viewport and proportionally worse below it (decision 2); a 64px
          rail hands that width back to the map where it hurts, and the canvas
          230px returns at xl (>=1280). Pure CSS on the viewport — the `xl:`
          variant, no JS breakpoint (breakpoint.ts is for click-handler
          behaviour, not style). */}
      <aside className="hidden flex-none flex-col border-r border-raad-border bg-raad-deep pb-4 pt-5 lg:flex lg:w-16 lg:min-h-0 lg:overflow-y-auto lg:px-2 xl:w-[230px] xl:px-3">
        <div className="flex items-center gap-2.5 lg:justify-center lg:px-0 xl:justify-start xl:px-2">
          {/* REBRAND 28 Oct 2026 — the "Raad Track" lockup. At the lg icon-rail width
              only the mark fits; at xl the full mark + wordmark shows. */}
          <span className="xl:hidden">
            <RaadLogo size={30} />
          </span>
          <span className="hidden xl:inline-flex">
            <RaadLogo size={28} wordmark title="Raad Track" />
          </span>
        </div>
        <div className="mt-5 flex-1">{Nav}</div>
        <div className="mt-2 flex items-center gap-2.5 border-t border-raad-border pt-3 lg:justify-center lg:px-0 xl:justify-start xl:px-2">
          <span className="flex size-8 flex-none items-center justify-center rounded-full bg-raad-primary font-display text-xs font-bold text-raad-onprimary">
            {initials(name)}
          </span>
          <div className="hidden min-w-0 xl:block">
            <div className="truncate text-xs font-semibold">{name}</div>
            <div className="text-[10.5px] text-raad-dim">
              {roleLabel} · {langLabel}
            </div>
          </div>
        </div>
      </aside>

      {/* 🔴 `lg:h-dvh` — A DEFINITE HEIGHT, AND THE WHOLE FAMILY TURNED ON ITS
           ABSENCE (17l). Every box on this chain was `min-h-dvh` / `flex-1`:
           floors, never a ceiling. `flex-1` only DISTRIBUTES space when its
           container has a definite size, so with nothing definite anywhere the
           column sized to its content, `min-h-0` had nothing to shrink toward,
           and an `overflow-hidden` on a route root could never engage. That is
           the Zones sheet 3635px down a phone AND desktop /trips scrolling.

           17k and the first 17l both worked around it by SUBTRACTING a measured
           chrome height from 100dvh. That number is not stable — measured 132px
           and 75px on the same build at the same size, because the chrome block's
           children are conditional — and any overestimate is Mahad's strip with
           black beneath it. Reproduced by forcing it to 600px: frame 300px, 468px
           of black.

           Setting the height here means no route has to compute anything.
           `lg:` only: the phone column must stay free to grow, and document
           routes are unaffected either way because `main` is `overflow: visible`
           — content taller than the frame still grows the page exactly as before,
           it is simply no longer able to grow the FRAME. */}
      <div className="flex min-h-dvh flex-1 flex-col lg:h-full lg:min-h-0">
        {/* Everything above the route outlet, wrapped as ONE measured block so a
            route can size itself against it — see `--raad-chrome` above. */}
        <div ref={chromeRef}>
          {/* Pay-to-restore bar for a suspended (unpaid) tenant (slice 11b).
              INSIDE the measured chrome, so it pushes the route down rather than
              covering the live-map controls. Renders nothing for a writable
              tenant. */}
          <SuspendedBanner />
          {/* Offline honesty bar + install affordance — both render nothing until
              they apply (offline / installable), so neither is a dead control. */}
          <OfflineBanner />
          <InstallPrompt />

        {/* Top bar — ONE bordered row (canvas 1i): greeting/date/status on the
            left, search + bell + controls on the right. REBRAND 28 Oct 2026 — standard
            SaaS density (~56px): compact py, 36px icon buttons, a ~30px EN|SO toggle and
            a ghost Sign out. The row height is MEASURED into `--raad-chrome`, so the map
            and list heights (calc(100dvh - --raad-chrome)) follow automatically. */}
        <header className="flex items-center gap-3 border-b border-raad-border px-4 py-2 sm:px-6">
          {/* LEFT. On the Overview the greeting stands here (all viewports); on
              every other route the mobile brand shows (the desktop sidebar carries
              the brand, so desktop non-Overview simply leaves the left empty). */}
          {isOverview ? (
            <div className="min-w-0 flex-1">
              <OverviewGreeting />
            </div>
          ) : (
            <div className="flex items-center lg:hidden">
              <RaadLogo size={28} wordmark title="Raad Track" />
            </div>
          )}

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {/* Fleet search (canvas 1i) — right-aligned in the bar, desktop only.
                The phone keeps its search in the content body (the mobile top bar
                is too narrow for a search field beside the greeting). */}
            {isOverview ? (
              <div className="hidden lg:block">
                <OverviewSearch />
              </div>
            ) : null}
            <div className="hidden lg:block">
              <LangToggle />
            </div>
            {/* The bell (canvas 1i) — a REAL destination to the Alerts feed, with
                the unread badge. Desktop only: the phone's unread indicator is the
                bottom tab bar's Alerts badge, so a second bell would be redundant.
                The count is the SHARED `unread` below — never a second recount. */}
            <Link
              to="/alerts"
              aria-label={unread > 0 ? t('alerts.unreadCount', { count: unread }) : t('nav.alerts')}
              className="relative hidden size-9 items-center justify-center rounded-lg border border-raad-border bg-raad-panel text-raad-muted transition-colors hover:border-raad-line-20 hover:text-raad-text lg:inline-flex"
            >
              <NavIcon d={ICONS.alerts} />
              {unread > 0 ? (
                <span className="absolute -right-1.5 -top-1.5 min-w-5 rounded-full bg-raad-primary px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-raad-onprimary">
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : null}
            </Link>
            {/* Push notifications control (slice 13b). Renders NOTHING until the
                runtime VAPID key resolves and only when the platform can deliver —
                absent, never disabled, when unconfigured (AC-12). Reachable on
                every viewport, so the Android fleet manager can find it. */}
            <PushToggle />
            {/* Settings on the phone (slice 16). `lg:hidden` because desktop has
                the sidebar entry — one door per viewport, never two. Without
                this, changing your password would be a desktop-only capability
                on a product built for an Android fleet manager. */}
            <Link
              to="/settings"
              aria-label={t('nav.settings')}
              className="inline-flex size-9 items-center justify-center rounded-lg border border-raad-border bg-raad-panel text-raad-muted transition-colors hover:border-raad-line-20 hover:text-raad-text lg:hidden"
            >
              <NavIcon d={ICONS.settings} />
            </Link>
            {/* Sign out — a smaller GHOST text button (secondary), kept visible. */}
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              className="min-h-9 rounded-lg px-3 text-[13px] font-semibold text-raad-muted transition-colors hover:bg-raad-fill-3 hover:text-raad-text disabled:opacity-60"
            >
              {t('shell.signOut')}
            </button>
          </div>
        </header>
        </div>

        {/* `lg:min-h-0` IS WHAT LETS A ROUTE BE A SCREEN (17l). A flex item defaults
            to `min-height: auto` and so refuses to shrink below its content — with
            that in force, a route root asking for `flex-1` still grows to its
            content and any `overflow-hidden` on it never engages. That is the root
            of the Zones-sheet and desktop-/trips defects both. Scoped to `lg:`
            because the phone layouts size themselves and do not want a second
            constraint; and it does NOT clip document routes, whose content is
            `overflow: visible` and still grows the page as before. */}
        {/* Reserve EXACTLY the MobileNav's height — its content (`--raad-nav-h`)
            plus the device safe-area (`--raad-safe-b`), counted ONCE. The old
            `pb-20` (80px) over a 52px nav left a grey strip when the inset was 0,
            and stacked a second safe-area when it was not (slice 56, item 9). */}
        <main className="flex flex-1 flex-col pb-[calc(var(--raad-nav-h)+var(--raad-safe-b))] lg:min-h-0 lg:overflow-y-auto lg:pb-0">
          <Outlet />
        </main>
      </div>

      {MobileNav}
    </div>
    </>
  );
}
